<template>
    <section id="skills" class="skills-component page-section">
        <div class="container">
            <header class="main-title title">Programming skills</header>
            <dl class="skills-summary">
                <div v-for="group in summaryGroups" :key="group.title" class="summary-row">
                    <dt>{{ group.title }}</dt>
                    <dd>{{ summaryText(group) }}</dd>
                </div>
            </dl>
            <div class="groups-toolbar">
                <button class="toggle-all" type="button" @click="toggleAll">
                    {{ isAllOpen ? 'Collapse all' : 'Expand all' }}
                </button>
            </div>
            <div class="skill-groups" :class="{'reveal-ready': observing}">
                <div v-for="(group, groupIndex) in groups" :key="group.title" class="skill-group"
                     :data-index="groupIndex" :class="{open: group.open, visible: group.visible}">
                    <button class="group-header" type="button" :aria-expanded="String(group.open)"
                            :aria-controls="'skill-group-' + groupIndex" @click="group.open = !group.open">
                        <span class="group-title">{{ group.title }}</span>
                        <span class="group-preview" aria-hidden="true">
                            <img v-for="skill in previewSkills(group)" :key="skill.name"
                                 :src="getLogoPath(skill.logo)" alt="" class="mini-logo">
                            <span v-if="group.skills.length > previewSkills(group).length" class="more">
                                +{{ group.skills.length - previewSkills(group).length }}
                            </span>
                        </span>
                        <font-awesome-icon class="chevron" :icon="['fas', 'chevron-circle-down']"/>
                    </button>
                    <div :id="'skill-group-' + groupIndex" class="group-collapse">
                        <div class="group-body">
                            <ul class="tiles">
                                <li v-for="(skill, index) in group.skills" :key="skill.name" class="tile"
                                    :style="{transitionDelay: group.open ? 80 + index * 45 + 'ms' : '0ms'}">
                                    <div class="logo">
                                        <img v-if="skill.logo" :src="getLogoPath(skill.logo)" :alt="skill.name">
                                        <span v-else class="monogram">{{ skill.name.slice(0, 2) }}</span>
                                    </div>
                                    <div class="name">{{ skill.name }}</div>
                                    <div v-if="skill.note" class="note">{{ skill.note }}</div>
                                </li>
                            </ul>
                        </div>
                    </div>
                </div>
            </div>
            <a href="/CV/Grades.pdf" class="download" download="Grades-Alexander Gorbatovsky">Download grades</a>
        </div>
    </section>
</template>

<script>
    export default {
        name: "SkillsComponent",
        data() {
            return {
                observing: false,
                // Groups are ordered by importance; so are the skills inside each group.
                // The plain-text list on top (for recruiters and ATS) is built from the same data:
                // groups with `inList: false` are left out, skills may define a longer `fullName`.
                // A skill without `logo` is rendered as a monogram until an image is added to assets/skills.
                // Icons named concept-*.svg are generic Lucide icons used for practices without a brand logo.
                groups: [
                    {
                        title: 'AI',
                        skills: [
                            {name: 'MCP', fullName: 'Model Context Protocol (MCP)', logo: 'mcp.svg', note: 'Model Context Protocol'},
                            {name: 'LLMs', logo: 'concept-llm.svg'},
                            {name: 'AI Agents', logo: 'concept-ai-agents.svg'},
                            {name: 'AI Engineering', logo: 'concept-ai-engineering.svg'},
                            {name: 'AI-Assisted Development', logo: 'concept-ai-assisted-dev.svg'},
                            {name: 'Tool Calling', logo: 'concept-tool-calling.svg'},
                            {name: 'Agent Skills', logo: 'concept-agent-skills.svg'},
                            {name: 'Multi-Agent Workflows', logo: 'concept-multi-agent.svg'}
                        ]
                    },
                    {
                        title: 'AI Coding Tools',
                        skills: [
                            {name: 'Claude Code', logo: 'claudecode.svg'},
                            {name: 'Cursor', logo: 'cursor.svg'},
                            {name: 'GitHub Copilot', logo: 'githubcopilot.svg'},
                            {name: 'Augment', fullName: 'Augment (AI agents & workflow automation)', logo: 'augment.svg', note: 'AI agents & workflow automation'}
                        ]
                    },
                    {
                        title: 'Frontend',
                        skills: [
                            {name: 'Vue.js', logo: 'vuejs.png'},
                            {name: 'Nuxt.js', logo: 'nuxt.svg'},
                            {name: 'JavaScript', logo: 'js.png'},
                            {name: 'HTML5', logo: 'html5.png'},
                            {name: 'CSS3', logo: 'css3.png'},
                            {name: 'SCSS', logo: 'scss.png'},
                            {name: 'Bootstrap', logo: 'bootstrap.png'},
                            {name: 'Micro Frontends', logo: 'concept-micro-frontends.svg'}
                        ]
                    },
                    {
                        title: 'Backend',
                        skills: [
                            {name: 'Node.js', logo: 'nodejs.png'},
                            {name: 'Express.js', logo: 'express.svg'},
                            {name: 'Python', logo: 'python.svg'},
                            {name: 'PHP', logo: 'php.svg'},
                            {name: 'RabbitMQ', logo: 'rabbitmq.png'},
                            {name: 'Microservices', logo: 'concept-microservices.svg'},
                            {name: 'Software Architecture', logo: 'concept-architecture.svg'}
                        ]
                    },
                    {
                        title: 'Databases & Search',
                        skills: [
                            {name: 'PostgreSQL', logo: 'postgresql.svg'},
                            {name: 'MySQL', logo: 'mysql.png'},
                            {name: 'MongoDB', logo: 'mongo.png'},
                            {name: 'Redis', logo: 'redis.svg'},
                            {name: 'OpenSearch', logo: 'opensearch.svg'}
                        ]
                    },
                    {
                        title: 'Cloud & DevOps',
                        skills: [
                            {name: 'AWS', fullName: 'Amazon Web Services (AWS)', logo: 'aws.svg'},
                            {name: 'Azure', logo: 'azure.svg'},
                            {name: 'Docker', logo: 'docker.svg'},
                            {name: 'Kubernetes', logo: 'kubernetes.svg'},
                            {name: 'GitHub Actions', logo: 'githubactions.svg'},
                            {name: 'Jenkins', logo: 'jenkins.svg'},
                            {name: 'SonarQube', logo: 'sonarqube.svg'},
                            {name: 'CI/CD', logo: 'concept-cicd.svg'}
                        ]
                    },
                    {
                        title: 'Monitoring & Tools',
                        skills: [
                            {name: 'Git', fullName: 'Git (GitHub, Bitbucket)', logo: 'git.svg'},
                            {name: 'GitHub', logo: 'github.svg', inList: false},
                            {name: 'Bitbucket', logo: 'bitbucket.svg', inList: false},
                            {name: 'Jira', logo: 'jira.svg'},
                            {name: 'Confluence', logo: 'confluence.svg'},
                            {name: 'Grafana', logo: 'grafana.svg'},
                            {name: 'Kibana', logo: 'kibana.svg'},
                            {name: 'BetterStack', logo: 'betterstack.svg'}
                        ]
                    },
                    {
                        title: 'Earlier experience',
                        inList: false,
                        skills: [
                            {name: 'AngularJS', logo: 'angularjs.png'},
                            {name: 'Knockout.js', logo: 'knockout.png'},
                            {name: 'jQuery', logo: 'jq.png'},
                            {name: 'WordPress', logo: 'wordpress.png', note: 'Freelance projects'},
                            {name: 'XAMPP', logo: 'xampp.png'},
                            {name: 'SVG', logo: 'svg.png', note: 'Font icons at Hippotec'},
                            {name: 'Facebook API', logo: 'fbApi.png', note: 'Small projects'}
                        ]
                    },
                    {
                        title: 'Studies at HIT',
                        inList: false,
                        skills: [
                            {name: 'Java', logo: 'java.png', note: 'Grade: 92'},
                            {name: 'J2EE', logo: 'j2ee.png', note: 'Grade: 83'},
                            {name: 'Scala', logo: 'scala.png', note: 'Java course, 92'},
                            {name: 'C/C++', logo: 'cAndCPP.png', note: 'C: 76, C++: 81'},
                            {name: '.NET', logo: 'dotNET.png', note: 'Grade: 81'},
                            {name: 'Android', logo: 'android.png', note: 'Grade: 100'},
                            {name: 'jQuery Mobile', logo: 'jquerymobile.png', note: 'Android course, 100'}
                        ]
                    }
                ].map(group => ({...group, open: false, visible: false}))
            };
        },
        computed: {
            isAllOpen() {
                return this.groups.every(group => group.open);
            },
            summaryGroups() {
                return this.groups.filter(group => group.inList !== false);
            }
        },
        mounted() {
            this.setupReveal();
        },
        beforeDestroy() {
            if (this.observer) {
                this.observer.disconnect();
            }
        },
        methods: {
            summaryText(group) {
                return group.skills
                    .filter(skill => skill.inList !== false)
                    .map(skill => skill.fullName || skill.name)
                    .join(', ');
            },
            toggleAll() {
                const open = !this.isAllOpen;
                this.groups.forEach(group => {
                    group.open = open;
                });
            },
            // A short strip of logos shown in the header of a collapsed group.
            previewSkills(group) {
                return group.skills.filter(skill => skill.logo).slice(0, 8);
            },
            getLogoPath(logo) {
                return require(`@/assets/skills/` + logo);
            },
            setupReveal() {
                const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                if (reduceMotion || !('IntersectionObserver' in window)) {
                    return;
                }
                // `visible` lives in the component state: classes added to the DOM by hand
                // would be wiped the next time a group is opened or closed.
                this.observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            this.groups[Number(entry.target.dataset.index)].visible = true;
                            this.observer.unobserve(entry.target);
                        }
                    });
                }, {threshold: 0.15});
                this.$el.querySelectorAll('.skill-group').forEach((el) => this.observer.observe(el));
                this.observing = true;
            }
        }
    }
</script>

<style lang="scss" scoped>
    #skills.skills-component {
        display: flex;
        justify-content: center;

        .container {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            max-width: 1200px;
            width: 100%;

            .main-title {
                margin-bottom: var(--section-title-gap, 50px);
            }

            .skills-summary {
                width: 100%;
                max-width: 900px;
                margin: 0 0 60px;
                border-top: 2px solid #e9ecef;

                .summary-row {
                    display: flex;
                    align-items: baseline;
                    gap: 24px;
                    padding: 12px 0;
                    border-bottom: 2px solid #e9ecef;

                    dt {
                        flex: 0 0 180px;
                        font-weight: bold;
                        color: #2c3e50;
                    }

                    dd {
                        margin: 0;
                        line-height: 1.6;
                        color: #555;
                        font-weight: 600;
                    }
                }
            }

            .groups-toolbar {
                display: flex;
                justify-content: flex-end;
                width: 100%;
                margin-bottom: 10px;

                .toggle-all {
                    padding: 0;
                    font-size: 0.95em;
                    color: #2f5ad6;
                    background: none;
                    border: none;
                    cursor: pointer;

                    &:hover, &:focus-visible {
                        text-decoration: underline;
                        outline: none;
                    }
                }
            }

            .skill-groups {
                width: 100%;
                border-top: 2px solid #e9ecef;

                .skill-group {
                    border-bottom: 2px solid #e9ecef;

                    .group-header {
                        display: flex;
                        align-items: center;
                        gap: 30px;
                        width: 100%;
                        padding: 18px 0;
                        font: inherit;
                        color: inherit;
                        text-align: left;
                        background: none;
                        border: none;
                        cursor: pointer;

                        .group-title {
                            flex: 0 0 200px;
                            font-weight: bold;
                            font-size: 1.2em;
                            color: #2c3e50;
                            transition: color .2s ease;
                        }

                        .group-preview {
                            display: flex;
                            flex: 1;
                            align-items: center;
                            gap: 10px;
                            min-width: 0;
                            opacity: 1;
                            transition: opacity .3s ease;

                            .mini-logo {
                                width: 28px;
                                height: 28px;
                                object-fit: contain;
                            }

                            .more {
                                font-size: 0.9em;
                                font-weight: 600;
                                color: #979a9c;
                            }
                        }

                        .chevron {
                            flex: none;
                            font-size: 1.4em;
                            color: #979a9c;
                            transition: transform .4s ease, color .2s ease;
                        }

                        &:hover, &:focus-visible {
                            outline: none;

                            .group-title, .chevron {
                                color: #3fb7ad;
                            }
                        }
                    }

                    // Height animates through the grid row track: 0fr (closed) to 1fr (open).
                    .group-collapse {
                        display: grid;
                        grid-template-rows: 0fr;
                        transition: grid-template-rows .45s ease;

                        .group-body {
                            min-height: 0;
                            overflow: hidden;
                            padding-left: 230px;
                            visibility: hidden;
                            transition: visibility 0s linear .45s;
                        }
                    }

                    .tiles {
                        display: grid;
                        grid-template-columns: repeat(auto-fill, minmax(124px, 1fr));
                        gap: 22px 12px;
                        margin: 0;
                        padding: 4px 0 26px;
                        list-style: none;

                        .tile {
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            text-align: center;
                            opacity: 0;
                            transform: translateY(14px) scale(.94);
                            transition: opacity .4s ease-out, transform .4s ease-out;

                            .logo {
                                width: 72px;
                                height: 72px;
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                padding: 15px;
                                border: 2px solid #e9ecef;
                                border-radius: 50%;
                                background-color: #fff;

                                img {
                                    width: 100%;
                                    height: 100%;
                                    object-fit: contain;
                                }

                                .monogram {
                                    font-weight: bold;
                                    font-size: 1.3em;
                                    color: #2c3e50;
                                }
                            }

                            .name {
                                margin-top: 8px;
                                font-weight: bold;
                                font-size: 0.95em;
                                line-height: 1.3;
                            }

                            .note {
                                margin-top: 2px;
                                font-size: 0.82em;
                                line-height: 1.3;
                                color: #979a9c;
                            }
                        }
                    }

                    &.open {
                        .group-header {
                            .group-preview {
                                opacity: 0;
                            }

                            .chevron {
                                transform: rotate(180deg);
                            }
                        }

                        .group-collapse {
                            grid-template-rows: 1fr;

                            .group-body {
                                visibility: visible;
                                transition-delay: 0s;
                            }
                        }

                        .tiles .tile {
                            opacity: 1;
                            transform: none;
                        }
                    }
                }

                // Scroll reveal: each group row fades in once when it enters the viewport.
                &.reveal-ready {
                    .skill-group {
                        opacity: 0;
                        transform: translateY(16px);
                        transition: opacity .5s ease-out, transform .5s ease-out;

                        &.visible {
                            opacity: 1;
                            transform: none;
                        }
                    }
                }
            }

            a.download {
                margin-top: 60px;
            }
        }
    }

    @media (prefers-reduced-motion: reduce) {
        #skills.skills-component {
            .container {
                .skill-groups {
                    .skill-group {
                        .group-collapse, .tile, .chevron, .group-preview {
                            transition: none !important;
                        }
                    }
                }
            }
        }
    }

    /*media*/
    @media screen and (max-width: 770px) {
        #skills.skills-component {
            .container {
                .skills-summary {
                    .summary-row {
                        flex-direction: column;
                        gap: 4px;

                        dt {
                            flex: none;
                        }
                    }
                }

                .skill-groups {
                    .skill-group {
                        .group-header {
                            flex-wrap: wrap;
                            gap: 10px 14px;

                            .group-title {
                                flex: 1;
                            }

                            .group-preview {
                                order: 3;
                                flex: 1 0 100%;
                            }
                        }

                        &.open {
                            .group-header {
                                .group-preview {
                                    display: none;
                                }
                            }
                        }

                        .group-collapse {
                            .group-body {
                                padding-left: 0;
                            }
                        }

                        .tiles {
                            grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
                        }
                    }
                }
            }
        }
    }
</style>
